/**
 * Phase 23: Billing API surface.
 */

import { supabase } from '@/lib/supabase';

export type SubscriptionStatus =
  | 'trialing' | 'active' | 'past_due' | 'canceled'
  | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'paused';

export interface Subscription {
  organization_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  tier: 'starter' | 'pro' | 'enterprise';
  status: SubscriptionStatus;
  quantity: number;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_start: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
}

type ApiResult<T> = { data: T; error: null } | { data: null; error: string };
const ok = <T>(data: T): ApiResult<T> => ({ data, error: null });
const err = <T = never>(message: string): ApiResult<T> => ({ data: null, error: message });

export async function getWorkspaceSubscription(
  orgId: string,
): Promise<ApiResult<Subscription | null>> {
  if (!supabase) return err('Not authenticated');
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('organization_id', orgId)
    .maybeSingle();
  if (error) return err(error.message);
  return ok((data as Subscription | null) ?? null);
}

export interface CheckoutSessionRequest {
  organizationId: string;
  priceId: string;
}

export async function createCheckoutSession(
  req: CheckoutSessionRequest,
): Promise<ApiResult<{ url: string }>> {
  if (!supabase) return err('Not authenticated');
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: req,
  });
  if (error) return err(error.message);
  if (!data || typeof data.url !== 'string') return err('Invalid checkout session response');
  return ok({ url: data.url });
}

export interface PortalSessionRequest {
  organizationId: string;
  returnUrl: string;
}

export async function createPortalSession(
  req: PortalSessionRequest,
): Promise<ApiResult<{ url: string }>> {
  if (!supabase) return err('Not authenticated');
  const { data, error } = await supabase.functions.invoke('create-portal-session', {
    body: req,
  });
  if (error) return err(error.message);
  if (!data || typeof data.url !== 'string') return err('Invalid portal session response');
  return ok({ url: data.url });
}

export async function syncSeats(orgId: string): Promise<ApiResult<{ quantity: number }>> {
  if (!supabase) return err('Not authenticated');
  const { data, error } = await supabase.functions.invoke('sync-seats', {
    body: { organizationId: orgId },
  });
  if (error) return err(error.message);
  if (!data || typeof data.quantity !== 'number') return err('Invalid sync-seats response');
  return ok({ quantity: data.quantity });
}
